import { PageMeta } from "@/components/PageMeta";
import { PolicyLayout, type PolicySection } from "@/components/PolicyLayout";
import { siteConfig } from "@/config/site";
import { routeSeo } from "@/content/seo";

const sections: PolicySection[] = [
  { id: "acceptance", label: "1. Acceptance of these Terms" },
  { id: "eligibility", label: "2. Eligibility" },
  { id: "accounts", label: "3. Accounts" },
  { id: "product-stage", label: "4. Product stage and pilot services" },
  { id: "permitted-use", label: "5. Permitted use" },
  { id: "responsibilities", label: "6. Your responsibilities" },
  { id: "user-content", label: "7. User content and code" },
  { id: "ip", label: "8. Intellectual property" },
  { id: "third-party", label: "9. Third-party services and extensions" },
  { id: "ai", label: "10. AI-assisted functionality" },
  { id: "fees", label: "11. Fees, billing and taxes" },
  { id: "cancellation", label: "12. Cancellation, suspension and termination" },
  { id: "changes", label: "13. Changes to the Service" },
  { id: "feedback", label: "14. Feedback" },
  { id: "disclaimers", label: "15. Disclaimers" },
  { id: "liability", label: "16. Limitation of liability" },
  { id: "governing-law", label: "17. Governing law and jurisdiction" },
  { id: "changes-to-terms", label: "18. Changes to these Terms" },
  { id: "contact", label: "19. Contact" },
];

export function TermsPage() {
  const seo = routeSeo["/legal/terms"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/legal/terms" />
      <PolicyLayout
        slug="terms"
        title="Terms of Service"
        intro="These Terms govern your use of the Devory website, Devory Cloud and, as they become available, the Devory desktop application and related services (together, the 'Service')."
        sections={sections}
      >
        <h2 id="acceptance">1. Acceptance of these Terms</h2>
        <p>
          The Service is provided by {siteConfig.legalEntity.name}, a {siteConfig.legalEntity.structure.toLowerCase()}{" "}
          based in {siteConfig.legalEntity.jurisdiction}, trading as {siteConfig.legalEntity.tradingAs} and{" "}
          {siteConfig.legalDeveloper} ("Devory", "we", "us"). By accessing or using the Service, you agree to
          these Terms. If you are using
          the Service on behalf of an organisation, you represent that you have authority to bind
          that organisation, and "you" refers to that organisation.
        </p>

        <h2 id="eligibility">2. Eligibility</h2>
        <p>
          You must be able to form a legally binding contract to use the Service. The Service is not
          directed at children, and Devory does not knowingly allow children to create accounts.
        </p>

        <h2 id="accounts">3. Accounts</h2>
        <p>
          Public self-service account creation is available at app.devory.app. You are responsible
          for the accuracy of your account information and for keeping your credentials
          confidential. Access to any additional non-public parts of Devory (for example, its
          internal engineering pilot) is granted individually and may be limited, revoked, or
          changed at Devory's discretion during this stage of development.
        </p>

        <h2 id="product-stage">4. Product stage and pilot services</h2>
        <p>
          Devory is in active development. Some features described on this website are available
          only in a controlled internal pilot, in active development, or planned, as labelled on the{" "}
          <a href="/product">product page</a>. Pilot and beta functionality is provided "as is," may
          change or be discontinued at any time, and should not be relied on for production use
          unless Devory confirms otherwise in writing for your specific engagement.
        </p>

        <h2 id="permitted-use">5. Permitted use</h2>
        <p>
          You may use the Service only in compliance with these Terms, the{" "}
          <a href="/legal/acceptable-use">Acceptable Use Policy</a>, and applicable law. You may not
          reverse engineer, resell, sublicense, or use the Service to build a directly competing
          product, except to the extent such restriction is not enforceable under applicable law.
        </p>

        <h2 id="responsibilities">6. Your responsibilities</h2>
        <p>
          You are responsible for the accuracy of information you provide to Devory, for maintaining
          the confidentiality of any credentials issued to you, and for activity that occurs through
          your use of the Service.
        </p>

        <h2 id="user-content">7. User content and code</h2>
        <p>
          As between you and Devory, you retain ownership of the software, graphs, components, and
          other content you create using Devory ("User Content"). You grant Devory a limited licence
          to host, process, and display your User Content solely to operate and support the Service
          for you. Devory does not claim ownership of your code or projects.
        </p>

        <h2 id="ip">8. Intellectual property</h2>
        <p>
          Devory and its licensors retain all rights, title, and interest in the Service itself,
          including its software, design, trademarks, and documentation, other than your User
          Content. Nothing in these Terms transfers any Devory intellectual property to you except
          the limited rights needed to use the Service as permitted.
        </p>

        <h2 id="third-party">9. Third-party services and extensions</h2>
        <p>
          The Service may in the future support extensions, plugins, MCP integrations, or other
          third-party tools. Third-party components are provided by their respective authors, are
          subject to their own terms, and are not vetted or warranted by Devory unless explicitly
          stated.
        </p>

        <h2 id="ai">10. AI-assisted functionality</h2>
        <p>
          Where Devory provides AI-assisted or agent-operable functionality, that functionality
          operates under explicit permission and confirmation boundaries described on the{" "}
          <a href="/security">security page</a>. AI-assisted output may be inaccurate or incomplete;
          you remain responsible for reviewing and validating any output before relying on it.
        </p>

        <h2 id="fees">11. Fees, billing and taxes</h2>
        <p>
          This website does not itself charge fees or process payment. Paid Devory Cloud plans are
          purchased and billed from inside Devory Cloud (Settings &gt; Billing) through Stripe, our
          payment processor. Applicable fees, billing frequency, and renewal terms are shown to you
          before you pay. {siteConfig.legalEntity.name} is not currently VAT-registered, so no UK VAT
          is added to the prices shown. You remain responsible for any taxes applicable to your
          purchase in your own jurisdiction. If {siteConfig.legalEntity.name}'s VAT-registration
          status changes, this section and the displayed prices will be updated to reflect that
          before it takes effect.
        </p>

        <h2 id="cancellation">12. Cancellation, suspension and termination</h2>
        <p>
          See the <a href="/legal/cancellation">Cancellation Policy</a> for how subscription
          cancellation works. Devory may suspend or terminate access to the Service, including
          pilot access, for violation of these Terms, the Acceptable Use Policy, or applicable law,
          or where continued access would create a security or legal risk.
        </p>

        <h2 id="changes">13. Changes to the Service</h2>
        <p>
          Because Devory is under active development, features, interfaces, and availability may
          change, be added, or be removed at any time, particularly for pilot and in-development
          capabilities. Devory acts reasonably in how it communicates material changes affecting
          active customers.
        </p>

        <h2 id="feedback">14. Feedback</h2>
        <p>
          If you send Devory feedback or suggestions, you agree Devory may use them without
          obligation or compensation to you.
        </p>

        <h2 id="disclaimers">15. Disclaimers</h2>
        <p>
          The Service is provided "as is" and "as available" without warranties of any kind, express
          or implied, to the maximum extent permitted by law, including any implied warranties of
          merchantability, fitness for a particular purpose, and non-infringement.
        </p>

        <h2 id="liability">16. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, Devory will not be liable for indirect,
          incidental, special, consequential, or punitive damages, or for loss of data, profits, or
          business, arising from your use of the Service. This limitation applies regardless of the
          legal theory of liability and even if Devory has been advised of the possibility of such
          damages.
        </p>

        <h2 id="governing-law">17. Governing law and jurisdiction</h2>
        <p>
          These Terms, and any dispute or claim arising out of or in connection with them, including
          non-contractual disputes or claims, are governed by the laws of England and Wales.
        </p>
        <p>
          If you are contracting with us in the course of a business, trade or profession, the courts
          of England and Wales will have exclusive jurisdiction over any dispute or claim arising out
          of or in connection with these Terms.
        </p>
        <p>
          If you are contracting as a consumer, you may have rights under mandatory
          consumer-protection laws that apply in the country where you live. Nothing in these Terms
          is intended to deprive you of those rights or prevent you from bringing proceedings in any
          court that has jurisdiction under applicable law.
        </p>

        <h2 id="changes-to-terms">18. Changes to these Terms</h2>
        <p>
          Devory may update these Terms as the Service develops. Material changes will update the
          "Last updated" date above and will be communicated to active account holders through the
          Service or by email.
        </p>

        <h2 id="contact">19. Contact</h2>
        <p>
          Questions about these Terms can be sent to{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a> or through the{" "}
          <a href="/contact">contact form</a>.
        </p>
      </PolicyLayout>
    </>
  );
}
